import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { getSplashTheme } from "@/lib/splashThemes";
import { SplashWordAnimation } from "./SplashWordAnimation";

const KEY = "757-splash-seen";
const TOTAL_MS = 4700;
const STRIPE = 300 / 13;
// Wavy top edge for the flag "pour" that rises inside the letters.
const WAVE = `M0 18 ${Array.from({ length: 16 }, (_, i) => `Q${i * 125 + 62.5} ${i % 2 ? 36 : 0} ${(i + 1) * 125} 18`).join(" ")} V420 H0 Z`;

const FlagDefs = () => (
  <defs>
    <pattern id="sp-stars" width="34" height="26" patternUnits="userSpaceOnUse">
      <polygon points="9,3 10.8,8.4 16.4,8.4 11.8,11.7 13.6,17 9,13.8 4.4,17 6.2,11.7 1.6,8.4 7.2,8.4" fill="#fff" />
      <polygon points="26,16 27.8,21.4 33.4,21.4 28.8,24.7 30.6,30 26,26.8 21.4,30 23.2,24.7 18.6,21.4 24.2,21.4" fill="#fff" />
    </pattern>
    <pattern id="sp-flag" width="1000" height="300" patternUnits="userSpaceOnUse">
      {Array.from({ length: 13 }, (_, i) => <rect key={i} y={i * STRIPE} width="1000" height={STRIPE + 0.5} fill={i % 2 ? "#ffffff" : "#B22234"} />)}
      <rect width="420" height={STRIPE * 7} fill="#3C3B6E" />
      <rect width="420" height={STRIPE * 7} fill="url(#sp-stars)" />
      <animateTransform attributeName="patternTransform" type="translate" values="0 0; -18 0; 0 0" dur="3s" repeatCount="indefinite" />
    </pattern>
    <linearGradient id="sp-shine" x1="0" x2="1" y1="0" y2="0">
      <stop offset="0" stopColor="#fff" stopOpacity="0" />
      <stop offset="0.5" stopColor="#fff" stopOpacity="0.75" />
      <stop offset="1" stopColor="#fff" stopOpacity="0" />
    </linearGradient>
    <clipPath id="sp-rise">
      <path d={WAVE}>
        <animateTransform attributeName="transform" type="translate" dur="1.7s" begin="0.9s" fill="freeze"
          values="0 300; -500 140; -1000 -40" keyTimes="0;0.55;1" />
      </path>
    </clipPath>
    <clipPath id="sp-text"><text x="500" y="200" textAnchor="middle" textLength="930" lengthAdjust="spacingAndGlyphs" className="splash-word">757 REALTY</text></clipPath>
  </defs>
);

const Jet = () => (
  <div className="splash-jet" aria-hidden="true">
    <div className="splash-trail"><span className="bg-[#B22234]" /><span className="bg-white" /><span className="bg-[#3C5BBF]" /></div>
    <svg viewBox="0 0 200 60" className="w-[clamp(90px,14vw,190px)] drop-shadow-[0_6px_14px_rgba(0,0,0,0.5)]">
      <circle cx="6" cy="30" r="7" fill="#FFB347" opacity="0.85" className="splash-burner" />
      <path d="M196 30 L150 24 L120 22 L96 7 L84 7 L91 22 L52 23 L36 9 L25 9 L29 25 L8 27 L4 30 L8 33 L29 35 L25 51 L36 51 L52 37 L91 38 L84 53 L96 53 L120 38 L150 36 Z" fill="#D9E1EA" />
      <path d="M120 22 L96 7 L84 7 L91 22 Z M36 9 L25 9 L29 25 L52 23 Z" fill="#AFBBC8" />
      <ellipse cx="152" cy="27" rx="15" ry="4.2" fill="#1B2D55" />
      <circle cx="70" cy="30" r="3.2" fill="#3C3B6E" /><circle cx="70" cy="30" r="1.4" fill="#fff" />
    </svg>
  </div>
);

const SeasonalAtmosphere = ({ themeId }) => {
  const symbol = themeId === "holiday-homecoming" ? "❄" : themeId === "autumn-home" ? "◆" : themeId === "spring-pcs" ? "✿" : themeId === "summer-coast" ? "✦" : themeId === "new-year" ? "✧" : "★";
  if (themeId === "patriotic") return null;
  return <div className={`splash-atmosphere splash-atmosphere-${themeId}`} aria-hidden="true" data-testid={`splash-atmosphere-${themeId}`}>{Array.from({ length: 28 }, (_, index) => <span key={index} style={{ left: `${(index * 19 + 4) % 100}%`, animationDelay: `${-((index * 0.43) % 6)}s`, animationDuration: `${5 + (index % 5)}s` }}>{symbol}</span>)}</div>;
};

/** Patriotic intro shown once per visit: outlined 757 REALTY fills with the flag while a jet flies over. */
export const SplashScreen = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(() => {
    try {
      return sessionStorage.getItem(KEY) !== "1" && !pathname.startsWith("/admin");
    } catch {
      return false;
    }
  });
  const [leaving, setLeaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [themeId, setThemeId] = useState("patriotic");
  const [themeReady, setThemeReady] = useState(false);
  const theme = getSplashTheme(themeId);
  const done = () => setLeaving(true);
  useEffect(() => { api.get("/splash-theme").then(({ data }) => setThemeId(data.theme_id)).catch(() => {}).finally(() => setThemeReady(true)); }, []);
  useEffect(() => {
    if (!show) return undefined;
    let live = true;
    const fontReady = document.fonts?.load ? document.fonts.load("700 132px Cinzel") : Promise.resolve();
    Promise.race([fontReady, new Promise((r) => setTimeout(r, 1000))]).catch(() => {}).then(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [show]);
  useEffect(() => {
    if (!show || !ready || !themeReady) return undefined;
    try {
      sessionStorage.setItem(KEY, "1");
    } catch {
      /* ignore */
    }
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => setLeaving(true), TOTAL_MS - 600);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [show, ready, themeReady]);
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => setShow(false), 650);
    return () => clearTimeout(t);
  }, [leaving]);
  if (!show || !themeReady) return null;

  return (
    <div className={`splash-root fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden ${leaving ? "splash-out" : ""}`} data-splash-theme={theme.id} data-testid="splash-screen">
      <div className="splash-stars" aria-hidden="true" />
      <SeasonalAtmosphere themeId={theme.id} />
      {ready && (<>
      {theme.id === "patriotic" ? <div className="relative z-10 w-full max-w-[1100px] px-4">
        <svg viewBox="0 0 1000 300" className="w-full" role="img" aria-label="757 Realty">
          <FlagDefs />
          <g className="splash-fill"><g clipPath="url(#sp-text)">
            <rect width="1000" height="300" fill="url(#sp-flag)" clipPath="url(#sp-rise)" />
            <rect className="splash-shine" x="-300" width="260" height="300" fill="url(#sp-shine)" />
          </g></g>
          <text x="500" y="200" textAnchor="middle" textLength="930" lengthAdjust="spacingAndGlyphs" className="splash-word splash-outline">757 REALTY</text>
        </svg>
        <Jet />
      </div> : <div className="relative z-10 w-full max-w-[1100px] px-4" data-testid={`seasonal-splash-${theme.id}`}><SplashWordAnimation themeId={theme.id} /><Jet /></div>}
      <div className="splash-rule relative z-10 mx-auto" aria-hidden="true" />
      <p className="splash-tagline splash-motto relative z-10 mt-4 text-center text-[12px] uppercase text-white/90 sm:text-[15px]" data-testid="splash-motto">{theme.motto}</p>
      <p className="splash-tagline splash-sub relative z-10 mt-2 text-center text-[15px] text-[#E6CF9A] [animation-delay:3.15s] sm:text-[17px]" data-testid="splash-subline">{theme.subline}</p>
      </>)}
      <button type="button" onClick={done} data-testid="splash-skip"
        className="absolute bottom-8 right-6 rounded-full border border-white/30 px-4 py-1.5 text-[12px] uppercase tracking-widest text-white/80 transition-colors hover:bg-white hover:text-[#0A1630]">
        Skip
      </button>
    </div>
  );
};
